import { createContext, Dispatch, ReactNode, SetStateAction, useEffect, useMemo, useState } from 'react';

import { User, onAuthStateChanged } from 'firebase/auth';

import { auth } from './firebase';
import UserApi from '../api/userProfileApi';
import UserProfileData from '../types/userProfileData';

interface AuthContextProps {
  user: User | null;
  userProfile?: UserProfileData | null;
  setUserProfile: Dispatch<SetStateAction<UserProfileData | null | undefined>>;
}

// TODO change default to object with null props
export const AuthContext = createContext<AuthContextProps | null>(null);

/**
 * Checks local storage if there is already a logged-in user. This function is meant to be used by the currentUser
 * state when setting the initial value and therefore needs to be available before the state is declared.
 * @return {User | null} A firebase user account object or null if there was no user found in local storage.
 */
function checkLocalStorage(): User | null {
  const storage = localStorage.getItem('authUser');
  return storage ? JSON.parse(storage) : null;
}

interface AuthProviderProps {
  children?: ReactNode | undefined;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(checkLocalStorage());
  const [currentUserProfile, setCurrentUserProfile] = useState<UserProfileData | null>();

  /**
   * This is a wrapper function for setCurrentUser. The reason this function is used instead of setCurrentUser for
   * onAuthStateChan is that setCurrentUser does not modify local storage which this one does. This is necessary if we
   * want the current user to be instantly available when opening a new window or refreshing.
   * See: https://stackoverflow.com/a/53539549
   * @param {User} user - A firebase user object.
   */
  function setCurrentUserAndLocalStorage(user: User | null): void {
    if (user === null) {
      localStorage.removeItem('authUser');
    } else {
      localStorage.setItem('authUser', JSON.stringify(user));
    }
    /* Since onAuthStateChange triggers several times in succession even without changing user, we check if the user has
    actually changed before updating currentUser. Otherwise, components that rely on the user re-render unnecessarily.
     */
    if (currentUser?.uid !== user?.uid) {
      setCurrentUser(user);
    }
  }

  onAuthStateChanged(auth, setCurrentUserAndLocalStorage);

  useEffect(() => {
    async function setUserData() {
      if (currentUser) {
        setCurrentUserProfile(await UserApi.getUserProfileByUuid(currentUser.uid));
      }
    }

    setUserData();
  }, [currentUser]);

  const payload: AuthContextProps = useMemo(() => {
    return {
      user: currentUser,
      userProfile: currentUserProfile,
      setUserProfile: setCurrentUserProfile,
    };
  }, [currentUser, currentUserProfile, setCurrentUserProfile]);

  return <AuthContext.Provider value={payload}>{children}</AuthContext.Provider>;
}
